import { useState, useMemo } from 'react';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  EyeSlashIcon,
  EyeIcon,
  InformationCircleIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

import {
  useKnowledgeList,
  useCreateKnowledge,
  useUpdateKnowledge,
  useDeleteKnowledge,
  useToggleKnowledgeActive,
} from '../hooks/useKnowledge';
import type {
  KnowledgeEntry,
  CreateKnowledgeData,
  UpdateKnowledgeData,
} from '../services/knowledgeService';
import KnowledgeFormModal from './KnowledgeFormModal';

const DOMAIN_LABELS: Record<string, string> = {
  business_process: 'Business Process',
  product: 'Product',
  customer: 'Customer',
  response: 'Response',
};

const DOMAIN_COLORS: Record<string, string> = {
  business_process: 'bg-brand/10 text-brand',
  product: 'bg-success/10 text-success',
  customer: 'bg-coral/10 text-coral',
  response: 'bg-warning/10 text-warning',
};

export default function KnowledgeList() {
  const [searchQuery, setSearchQuery] = useState('');
  const [domainFilter, setDomainFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [editingEntry, setEditingEntry] = useState<KnowledgeEntry | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<KnowledgeEntry | null>(
    null
  );

  const { data: entries = [], isLoading } = useKnowledgeList({
    domain: domainFilter || undefined,
    activeOnly: !showInactive,
  });

  const createMutation = useCreateKnowledge();
  const updateMutation = useUpdateKnowledge();
  const deleteMutation = useDeleteKnowledge();
  const toggleActiveMutation = useToggleKnowledgeActive();

  const filtered = useMemo(() => {
    if (!searchQuery) return entries;
    const q = searchQuery.toLowerCase();
    return entries.filter(
      (e) =>
        e.title.toLowerCase().includes(q) ||
        e.content.toLowerCase().includes(q) ||
        e.tags.some((t) => t.toLowerCase().includes(q))
    );
  }, [entries, searchQuery]);

  function handleOpenCreate() {
    setEditingEntry(null);
    setShowModal(true);
  }

  function handleOpenEdit(entry: KnowledgeEntry) {
    setEditingEntry(entry);
    setShowModal(true);
  }

  function handleSubmit(data: CreateKnowledgeData | UpdateKnowledgeData) {
    if (editingEntry) {
      updateMutation.mutate(
        { id: editingEntry.id, data: data as UpdateKnowledgeData },
        { onSuccess: () => setShowModal(false) }
      );
    } else {
      createMutation.mutate(data as CreateKnowledgeData, {
        onSuccess: () => setShowModal(false),
      });
    }
  }

  function handleDelete(entry: KnowledgeEntry) {
    deleteMutation.mutate(entry.id, {
      onSuccess: () => setConfirmDelete(null),
    });
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink">Knowledge Base</h1>
          <p className="mt-1 text-sm text-ink-3">
            {filtered.length} article{filtered.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowGuide((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-pill border border-line px-3 py-2 text-sm font-medium text-ink-2 hover:bg-black/[0.04]"
          >
            <InformationCircleIcon className="h-4 w-4" />
            Guide
          </button>
          <button
            onClick={handleOpenCreate}
            className="inline-flex items-center gap-2 rounded-pill bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-ink"
          >
            <PlusIcon className="h-4 w-4" />
            Add Entry
          </button>
        </div>
      </div>

      {/* Writing Guide */}
      {showGuide && (
        <div className="mb-6 rounded-card border border-brand/20 bg-brand/10 p-5">
          <div className="flex items-start justify-between">
            <h3 className="text-sm font-semibold text-brand">Writing Guide</h3>
            <button
              onClick={() => setShowGuide(false)}
              className="text-brand hover:text-brand-ink"
            >
              <ChevronDownIcon className="h-4 w-4 rotate-180" />
            </button>
          </div>

          <p className="mt-2 text-sm text-ink-2">
            Knowledge base entries are what the AI agent searches when answering customer questions. Well-written entries directly improve the quality of AI responses.
          </p>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-brand">Domains</h4>
              <ul className="mt-1.5 space-y-1 text-sm text-ink-2">
                <li><span className="font-medium">Business Process</span> &mdash; Policies, procedures, rules (return policy, shipping, warranty)</li>
                <li><span className="font-medium">Product</span> &mdash; Product details, sizing guides, compatibility, care instructions</li>
                <li><span className="font-medium">Customer</span> &mdash; FAQs, common questions, troubleshooting steps</li>
                <li><span className="font-medium">Response Template</span> &mdash; Pre-written responses the AI should use or adapt</li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-brand">Tips</h4>
              <ul className="mt-1.5 space-y-1 text-sm text-ink-2">
                <li><span className="font-medium">Title</span> &mdash; Use clear, searchable names (e.g., &ldquo;Return Policy&rdquo; not &ldquo;Policy 1&rdquo;)</li>
                <li><span className="font-medium">Content</span> &mdash; Write in the voice you want the AI to use. Be specific with numbers and conditions. Use bullet points for steps.</li>
                <li><span className="font-medium">Tags</span> &mdash; Add synonyms customers use (e.g., return, refund, exchange, send back, money back)</li>
                <li><span className="font-medium">One topic per entry</span> &mdash; Don&apos;t mix topics. The AI retrieves by relevance; mixed entries reduce accuracy.</li>
              </ul>
            </div>
          </div>

          <div className="mt-4 rounded-card border border-brand/20 bg-surface p-3">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-brand">Example: Good Entry</h4>
            <div className="mt-1.5 text-sm text-ink-2">
              <p><span className="font-medium">Domain:</span> Business Process &nbsp;|&nbsp; <span className="font-medium">Title:</span> Return and Refund Policy</p>
              <p className="mt-1"><span className="font-medium">Content:</span> Customers can return items within 14 days of delivery for a full refund. Items must be unused and in original packaging. Exceptions: Sale items are final sale. Personalized items cannot be returned unless defective. If the item is defective, we cover return shipping.</p>
              <p className="mt-1"><span className="font-medium">Tags:</span> return, refund, exchange, money back, send back, defective</p>
            </div>
          </div>

          <div className="mt-3 text-xs text-brand">
            Update existing entries rather than creating duplicates on the same topic. Test after adding by messaging the AI about the topic.
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search articles..."
            className="w-full rounded-xl border border-line bg-surface py-2 pl-9 pr-3 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20"
          />
        </div>
        <select
          value={domainFilter}
          onChange={(e) => setDomainFilter(e.target.value)}
          className="rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20"
        >
          <option value="">All Domains</option>
          <option value="business_process">Business Process</option>
          <option value="product">Product</option>
          <option value="customer">Customer</option>
          <option value="response">Response</option>
        </select>
        <label className="flex items-center gap-2 text-sm text-ink-2">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="rounded border-line text-brand focus:ring-brand/20"
          />
          Show inactive
        </label>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="py-12 text-center text-ink-3">Loading...</div>
      )}

      {/* Empty */}
      {!isLoading && filtered.length === 0 && (
        <div className="rounded-card border border-line bg-surface py-12 text-center shadow-s">
          <p className="text-ink-3">No knowledge entries found.</p>
          <button
            onClick={handleOpenCreate}
            className="mt-2 text-sm font-medium text-brand hover:text-brand-ink"
          >
            Create your first entry
          </button>
        </div>
      )}

      {/* Entry Cards */}
      {!isLoading && filtered.length > 0 && (
        <div className="space-y-3">
          {filtered.map((entry) => (
            <div
              key={entry.id}
              className={`rounded-card border bg-surface p-4 shadow-s transition hover:shadow-m ${
                !entry.active ? 'border-line opacity-60' : 'border-line'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="mb-1 flex items-center gap-2">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                        DOMAIN_COLORS[entry.domain] || 'bg-surface-2 text-ink-2'
                      }`}
                    >
                      {DOMAIN_LABELS[entry.domain] || entry.domain}
                    </span>
                    {!entry.active && (
                      <span className="inline-flex rounded-full bg-danger/10 px-2 py-0.5 text-xs font-medium text-danger">
                        Inactive
                      </span>
                    )}
                    <span className="text-xs text-ink-2">
                      v{entry.version}
                    </span>
                  </div>
                  <h3 className="font-semibold text-ink">{entry.title}</h3>
                  <p className="mt-1 line-clamp-2 text-sm text-ink-2">
                    {entry.content}
                  </p>
                  {entry.tags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {entry.tags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex rounded bg-surface-2 px-1.5 py-0.5 text-xs text-ink-2"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="ml-4 flex items-center gap-1">
                  <button
                    onClick={() => handleOpenEdit(entry)}
                    className="rounded p-1.5 text-ink-2 hover:bg-black/[0.04] hover:text-brand"
                    title="Edit"
                  >
                    <PencilSquareIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() =>
                      toggleActiveMutation.mutate({
                        id: entry.id,
                        active: !entry.active,
                      })
                    }
                    className="rounded p-1.5 text-ink-2 hover:bg-warning/10 hover:text-warning"
                    title={entry.active ? 'Deactivate' : 'Activate'}
                  >
                    {entry.active ? (
                      <EyeSlashIcon className="h-4 w-4" />
                    ) : (
                      <EyeIcon className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={() => setConfirmDelete(entry)}
                    className="rounded p-1.5 text-ink-2 hover:bg-danger/10 hover:text-danger"
                    title="Delete"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form Modal */}
      <KnowledgeFormModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleSubmit}
        isLoading={createMutation.isPending || updateMutation.isPending}
        entry={editingEntry}
      />

      {/* Delete Confirmation */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="w-full max-w-sm rounded-card bg-surface p-6 shadow-l">
            <h3 className="text-lg font-semibold text-ink">
              Delete Entry
            </h3>
            <p className="mt-2 text-sm text-ink-2">
              Are you sure you want to delete &ldquo;{confirmDelete.title}
              &rdquo;? This action cannot be undone.
            </p>
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="rounded-pill border border-line px-4 py-2 text-sm font-medium text-ink-2 hover:bg-black/[0.04]"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                disabled={deleteMutation.isPending}
                className="rounded-pill bg-danger px-4 py-2 text-sm font-medium text-white hover:bg-danger/90 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
